/**
 * @nanokart/tryon-studio-sdk
 * MainStage - Premium design with CSS animations (no framer-motion)
 *
 * Fixes:
 * 1. Add to Cart button always visible after try-on (fires hostAdapter + cartUrl)
 * 2. Camera prop wired through onCameraOpen
 * 3. Stage displays per-selected-product result (getCachedResult) — new item = customer photo
 * 4. onImageClick prop → opens lightbox in parent
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useTryOnConfig } from '../../core/TryOnProvider';
import { usePhoto } from '../../core/hooks/usePhoto';
import { useTryOn } from '../../core/hooks/useTryOn';
import { useInventory } from '../../core/hooks/useInventory';
import { useCart } from '../../core/hooks/useCart';
import { useUI } from '../../core/hooks/useUI';
import { useMoments } from '../../core/hooks/useMoments';

export interface MomentDisplay {
  url: string;
  label: string;
}

export interface MainStageProps {
  className?: string;
  style?: React.CSSProperties;
  onCameraOpen?: () => void;
  placeholder?: React.ReactNode;
  /** External moment to display (from history strip click). */
  externalMomentDisplay?: MomentDisplay | null;
  /** Called when user performs an action that should clear external overrides */
  onInteraction?: () => void;
  /** Called when user clicks the displayed image — parent can open a lightbox */
  onImageClick?: (imageUrl: string) => void;
}

const MOMENTS_CONFIG = [
  { id: 'creative_workday', label: 'Workday', icon: '💼' },
  { id: 'unwind', label: 'Unwind', icon: '🛋️' },
  { id: 'after_hours', label: 'After Hours', icon: '🌙' },
  { id: 'escape', label: 'The Escape', icon: '🌴' },
  { id: 'gentle_celebration', label: 'Celebrate', icon: '🎉' },
];

export const MainStage: React.FC<MainStageProps> = ({
  className,
  style,
  onCameraOpen,
  placeholder,
  externalMomentDisplay,
  onInteraction,
  onImageClick,
}) => {
  const { config, features, hostAdapter } = useTryOnConfig();
  const { preview, hasPhoto, setPhoto, clearPhoto, isUploading, openFilePicker, fileInputRef } = usePhoto();
  const { isGenerating, executeTryOn, error: tryonError, clearError, hasTriedOn, getCachedResult } = useTryOn();
  const { selectedItem } = useInventory();
  const { addToSoftCart, addToHardCart, isInSoftCart, isInHardCart, hasAddToCartWebhook, invokeAddToCartWebhook, recordPurchaseIntent } = useCart();
  const { showToast } = useUI();
  const { generateMoment, isGenerating: momentGenerating, hasMoment, getMomentResults } = useMoments();

  const [momentsOpen, setMomentsOpen] = useState(false);
  const [activeMoment, setActiveMoment] = useState<MomentDisplay | null>(null);

  const selectedProductId = selectedItem?.productId ?? '';

  // FIX 3: Use per-product cached result, not global currentResult
  // So when a new garment is selected without a try-on, stage shows customer photo
  const activeResult = selectedProductId ? getCachedResult(selectedProductId) : null;
  const hasCurrentTryOn = !!activeResult;
  const inHardCart = selectedProductId ? isInHardCart(selectedProductId) : false;

  // Display priority: external moment (history) > local moment > try-on result > customer photo
  const activeMomentDisplay = externalMomentDisplay ?? activeMoment;
  const displayImage = activeMomentDisplay?.url || activeResult?.resultImageUrl || preview;
  const showingMoment = !!activeMomentDisplay;

  const isLoadingState = isUploading || isGenerating;
  const showActionOverlay = hasPhoto && !isLoadingState && !!selectedItem;
  const showClearBtn = hasPhoto && !isLoadingState;
  const showUploadBtns = !hasPhoto && !isUploading;

  // FIX 3: Clear local moment when selected item changes (new dress selected)
  useEffect(() => {
    setActiveMoment(null);
    setMomentsOpen(false);
    onInteraction?.();
  }, [selectedProductId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear moment when photo is removed
  useEffect(() => {
    if (!hasPhoto) {
      setActiveMoment(null);
      setMomentsOpen(false);
      onInteraction?.();
    }
  }, [hasPhoto]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) { setPhoto(file); onInteraction?.(); }
  }, [setPhoto, onInteraction]);

  const handleTryOn = useCallback(async () => {
    if (!selectedItem) { showToast('Please select a garment first', 'info'); return; }
    if (!hasPhoto) { showToast('Please upload your photo first', 'info'); return; }
    setActiveMoment(null);
    onInteraction?.();
    try {
      const result = await executeTryOn(selectedItem.productId);
      if (result && !isInSoftCart(selectedItem.productId)) {
        addToSoftCart(selectedItem, result);
      }
    } catch {
      showToast('Try-on failed. Please try again.', 'error');
    }
  }, [selectedItem, hasPhoto, executeTryOn, isInSoftCart, addToSoftCart, showToast, onInteraction]);

  // FIX 1: Add to Cart — always visible after try-on, fires callback + opens URL
  const handleAddToCart = useCallback(async () => {
    if (!selectedItem) return;

    // Internal cart (only if feature enabled)
    if (features.cart) {
      addToHardCart(selectedItem);
    }

    // Record purchase intent for ROI analytics — always fire, regardless of webhook config
    recordPurchaseIntent(selectedItem, activeResult, selectedItem.sizes?.[0]);

    // Invoke Add to Cart webhook via SDK API (API key auth, loosely coupled)
    // This sends product + customer context to api.nanokart.ai which invokes partner's webhook
    if (hasAddToCartWebhook) {
      invokeAddToCartWebhook(selectedItem, activeResult, selectedItem.sizes?.[0]);
    }

    // Also fire hostAdapter event → triggers onAddToCart callback in host app
    try {
      await hostAdapter.addToCart({
        productId: selectedItem.productId,
        productName: selectedItem.name,
        variantId: selectedItem.variantId,
        size: selectedItem.sizes?.[0], // Default to first size if available
        color: selectedItem.color,
        quantity: 1,
        price: selectedItem.price,
        currency: selectedItem.currency,
        metadata: {
          tryonImageUrl: activeResult?.resultImageUrl,
          garmentImageUrl: selectedItem.imageUrl, // Add garment image URL
          ...(selectedItem.metadata ?? {}),
        },
      });
    } catch {
      // adapter errors are non-critical
    }

    // Open product URL if partner provided one in item metadata
    const cartUrl = selectedItem.metadata?.cartUrl as string | undefined;
    if (cartUrl) {
      window.open(cartUrl, '_blank', 'noopener,noreferrer');
    }

    showToast(`${selectedItem.name} added!`, 'success');
  }, [selectedItem, activeResult, features.cart, addToHardCart, hostAdapter, showToast, hasAddToCartWebhook, invokeAddToCartWebhook, recordPurchaseIntent]);

  const handleGenerateMoment = useCallback(async (momentId: string, momentLabel: string) => {
    if (!activeResult) { showToast('Complete a try-on first', 'info'); return; }

    // Already generated → show it on stage
    if (hasMoment(activeResult.cacheKey, momentId)) {
      const existingMoments = getMomentResults(activeResult.cacheKey);
      const existing = existingMoments.find(r => r.momentType === momentId);
      if (existing) {
        setActiveMoment({ url: existing.resultImageUrl, label: momentLabel });
        onInteraction?.();
      }
      return;
    }

    try {
      const result = await generateMoment(activeResult.cacheKey, momentId);
      if (result?.resultImageUrl) {
        setActiveMoment({ url: result.resultImageUrl, label: momentLabel });
        onInteraction?.();
      }
    } catch {
      showToast('Failed to generate moment', 'error');
    }
  }, [activeResult, generateMoment, hasMoment, getMomentResults, showToast, onInteraction]);

  const handleBackToTryon = useCallback(() => {
    setActiveMoment(null);
    onInteraction?.();
  }, [onInteraction]);

  // FIX 4: Click on displayed image opens lightbox
  const handleImageClick = useCallback(() => {
    if (displayImage && onImageClick) {
      onImageClick(displayImage);
    }
  }, [displayImage, onImageClick]);

  return (
    <div
      className={className}
      style={{ position: 'relative', ...style }}
      data-testid="main-stage"
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        data-testid="photo-input"
      />

      {/* Stage Area */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '3/4',
          maxHeight: '60vh',
          background: 'linear-gradient(to bottom, #f8fafc, #f1f5f9)',
          borderRadius: '16px',
          overflow: 'hidden',
          border: '1px solid #e2e8f0',
          cursor: displayImage ? 'zoom-in' : 'pointer',
        }}
        onClick={() => {
          if (displayImage) {
            handleImageClick();
          } else {
            openFilePicker();
          }
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const file = e.dataTransfer.files[0];
          if (file?.type.startsWith('image/')) { setPhoto(file); onInteraction?.(); }
        }}
        data-testid="stage-area"
      >
        {/* Stage Image or Placeholder */}
        {displayImage ? (
          <img
            key={displayImage}
            src={displayImage}
            alt={showingMoment ? `${activeMomentDisplay?.label} moment` : activeResult ? 'Try-on result' : 'Your photo'}
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'contain',
              animation: 'nk-fade-in 0.35s ease-out',
            }}
            data-testid="stage-image"
          />
        ) : (
          <div
            style={{
              position: 'absolute', inset: 0,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              padding: '24px', gap: '10px',
              animation: 'nk-fade-in 0.2s ease-out',
            }}
            data-testid="upload-placeholder"
          >
            {placeholder ? placeholder : (
              <>
                <div style={{
                  width: 64, height: 64, borderRadius: '50%',
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <p style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: '#64748b' }}>
                  Add your photo to start
                </p>
                <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>
                  Take a selfie or upload from gallery
                </p>
              </>
            )}
          </div>
        )}

        {/* Loading Overlay */}
        {isLoadingState ? (
          <div
            style={{
              position: 'absolute', inset: 0,
              backgroundColor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(4px)',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              gap: '16px', zIndex: 20,
              animation: 'nk-fade-in 0.2s ease-out',
            }}
            data-testid="loading-overlay"
          >
            <div style={{ position: 'relative', width: 56, height: 56 }}>
              <div style={{ width: 56, height: 56, border: '3px solid #dbeafe', borderRadius: '50%' }} />
              <div style={{
                position: 'absolute', inset: 0,
                border: '3px solid #2563eb',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'nk-spin 0.8s linear infinite',
              }} />
            </div>
            <p style={{ margin: 0, fontSize: '13px', color: '#475569', fontWeight: 500 }}>
              {isUploading ? 'Uploading...' : 'Creating your look...'}
            </p>
          </div>
        ) : null}

        {/* Error Overlay */}
        {tryonError ? (
          <div
            style={{
              position: 'absolute', inset: 0,
              backgroundColor: 'rgba(254,226,226,0.95)',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              gap: '12px', padding: '24px', zIndex: 20,
              animation: 'nk-fade-in 0.2s ease-out',
            }}
            data-testid="error-overlay"
          >
            <p style={{ margin: 0, fontWeight: 500, color: '#dc2626', textAlign: 'center', fontSize: '14px' }}>
              {tryonError}
            </p>
            <button
              onClick={(e) => { e.stopPropagation(); clearError(); }}
              style={{
                padding: '8px 16px', backgroundColor: '#fff',
                color: '#374151', border: 'none', borderRadius: '8px',
                fontSize: '13px', cursor: 'pointer',
              }}
            >
              Try Again
            </button>
          </div>
        ) : null}

        {/* Clear photo button (top-right X) */}
        {showClearBtn ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              clearPhoto();
              clearError();
              setMomentsOpen(false);
              setActiveMoment(null);
              onInteraction?.();
            }}
            style={{
              position: 'absolute', top: 10, right: 10,
              width: 32, height: 32, borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(4px)',
              border: 'none', cursor: 'pointer', zIndex: 15,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
              animation: 'nk-scale-in 0.2s ease-out',
            }}
            data-testid="clear-photo-button"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        ) : null}

        {/* Stage status badge (top-left) */}
        {displayImage && !isGenerating ? (
          showingMoment ? (
            <div
              style={{
                position: 'absolute', top: 10, left: 10,
                display: 'flex', alignItems: 'center', gap: 6,
                zIndex: 15, animation: 'nk-scale-in 0.2s ease-out',
              }}
            >
              <div
                style={{
                  padding: '4px 10px', borderRadius: '9999px',
                  backgroundColor: 'rgba(139,92,246,0.9)',
                  color: '#fff', fontSize: '11px', fontWeight: 600,
                }}
                data-testid="moment-badge"
              >
                {activeMomentDisplay?.label}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); handleBackToTryon(); }}
                style={{
                  padding: '4px 10px', borderRadius: '9999px',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  backdropFilter: 'blur(4px)',
                  color: '#475569', fontSize: '11px', fontWeight: 500,
                  border: 'none', cursor: 'pointer',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                }}
                data-testid="back-to-tryon-btn"
              >
                Back to Try-on
              </button>
            </div>
          ) : activeResult && !isGenerating ? (
            <div
              style={{
                position: 'absolute', top: 10, left: 10,
                padding: '4px 10px', borderRadius: '9999px',
                backgroundColor: 'rgba(16,185,129,0.9)',
                color: '#fff', fontSize: '11px', fontWeight: 600, zIndex: 15,
                animation: 'nk-scale-in 0.2s ease-out',
              }}
              data-testid="result-badge"
            >
              Try-on
            </div>
          ) : null
        ) : null}

        {/* Action Button Overlay (bottom of stage) */}
        {showActionOverlay ? (
          <div
            style={{
              position: 'absolute', bottom: 12, left: 12, right: 12,
              display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
              zIndex: 15,
              animation: 'nk-slide-up 0.25s ease-out',
            }}
          >
            {/* Left: Moments */}
            <div style={{ position: 'relative' }}>
              {hasCurrentTryOn && features.moments ? (
                <>
                  {momentsOpen ? (
                    <div
                      style={{
                        position: 'absolute', bottom: '100%', left: 0,
                        marginBottom: 8,
                        display: 'flex', flexDirection: 'column', gap: 6,
                        minWidth: 140,
                        animation: 'nk-slide-up 0.2s ease-out',
                      }}
                    >
                      {MOMENTS_CONFIG.map((moment) => {
                        const isLoadingMoment = momentGenerating === moment.id;
                        const isGenerated = activeResult
                          ? hasMoment(activeResult.cacheKey, moment.id)
                          : false;
                        return (
                          <button
                            key={moment.id}
                            onClick={(e) => { e.stopPropagation(); handleGenerateMoment(moment.id, moment.label); }}
                            disabled={isLoadingMoment}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '7px 14px',
                              borderRadius: '9999px', border: 'none',
                              fontSize: '12px', fontWeight: 500,
                              cursor: isLoadingMoment ? 'default' : 'pointer',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                              whiteSpace: 'nowrap' as const,
                              background: isGenerated
                                ? 'linear-gradient(135deg, #10b981, #059669)'
                                : 'rgba(255,255,255,0.95)',
                              backdropFilter: 'blur(8px)',
                              color: isGenerated ? '#fff' : '#334155',
                            }}
                            data-testid={`moment-btn-${moment.id}`}
                          >
                            {isLoadingMoment ? (
                              <div style={{
                                width: 14, height: 14,
                                border: '2px solid currentColor',
                                borderTopColor: 'transparent',
                                borderRadius: '50%',
                                animation: 'nk-spin 0.8s linear infinite',
                              }} />
                            ) : (
                              <span style={{ fontSize: '14px' }}>{moment.icon}</span>
                            )}
                            <span>{moment.label}</span>
                            {isGenerated ? (
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <polyline points="20,6 9,17 4,12" />
                              </svg>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}

                  <button
                    onClick={(e) => { e.stopPropagation(); setMomentsOpen(!momentsOpen); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '7px 14px',
                      borderRadius: '9999px', border: 'none', cursor: 'pointer',
                      fontSize: '12px', fontWeight: 500,
                      backgroundColor: momentsOpen ? 'rgba(251,191,36,0.2)' : 'rgba(255,255,255,0.95)',
                      backdropFilter: 'blur(8px)',
                      color: momentsOpen ? '#92400e' : '#475569',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    }}
                    data-testid="moments-overlay-btn"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={momentsOpen ? '#d97706' : '#f59e0b'} strokeWidth="2.5">
                      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                    </svg>
                    <span>Moments</span>
                    <svg
                      width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                      style={{ transform: momentsOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                    >
                      <polyline points="6,9 12,15 18,9" />
                    </svg>
                  </button>
                </>
              ) : null}
            </div>

            {/* Right: Try On or Add to Cart */}
            <div>
              {!hasCurrentTryOn ? (
                // No try-on yet → show Try it on Me
                <button
                  onClick={(e) => { e.stopPropagation(); handleTryOn(); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '9px 16px',
                    borderRadius: '9999px', border: 'none',
                    fontSize: '12px', fontWeight: 600,
                    background: 'linear-gradient(135deg, #1d4ed8, #2563eb)',
                    color: '#fff', cursor: 'pointer',
                    boxShadow: '0 4px 14px rgba(37,99,235,0.4)',
                  }}
                  data-testid="try-on-overlay-button"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                  </svg>
                  Try it on Me
                </button>
              ) : (hasAddToCartWebhook || features.cart) ? (
                // Has try-on AND (webhook configured OR cart feature enabled) → show Add to Cart
                <button
                  onClick={(e) => { e.stopPropagation(); if (!inHardCart || !features.cart) handleAddToCart(); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '9px 16px',
                    borderRadius: '9999px',
                    border: (inHardCart && features.cart) ? 'none' : '1px solid rgba(255,255,255,0.2)',
                    fontSize: '12px', fontWeight: 600,
                    background: (inHardCart && features.cart)
                      ? 'linear-gradient(135deg, #10b981, #059669)'
                      : 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(8px)',
                    color: (inHardCart && features.cart) ? '#fff' : '#1e293b',
                    cursor: (inHardCart && features.cart) ? 'default' : 'pointer',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                  }}
                  data-testid="add-to-cart-overlay-button"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <path d="M16 10a4 4 0 0 1-8 0" />
                  </svg>
                  {(inHardCart && features.cart) ? 'Added' : 'Add to Cart'}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Camera + Upload buttons (when no photo) */}
        {showUploadBtns ? (
          <div
            style={{
              position: 'absolute', bottom: 24, left: 0, right: 0,
              display: 'flex', justifyContent: 'center', gap: 14,
              zIndex: 10,
              animation: 'nk-slide-up 0.3s ease-out',
            }}
          >
            {onCameraOpen ? (
              <button
                onClick={(e) => { e.stopPropagation(); onCameraOpen(); }}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
                  padding: '10px 18px',
                  background: '#2563eb', color: '#fff',
                  borderRadius: '16px', border: 'none', cursor: 'pointer',
                  boxShadow: '0 8px 20px rgba(37,99,235,0.3)',
                  fontSize: '11px', fontWeight: 500,
                }}
                data-testid="camera-button"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
                <span>Camera</span>
              </button>
            ) : null}

            <button
              onClick={(e) => { e.stopPropagation(); openFilePicker(); }}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
                padding: '10px 18px',
                background: '#fff', color: '#475569',
                borderRadius: '16px', border: '1px solid #e2e8f0', cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                fontSize: '11px', fontWeight: 500,
              }}
              data-testid="upload-button"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21,15 16,10 5,21" />
              </svg>
              <span>Upload</span>
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default MainStage;
